// src/api/upcomingEvents.js

import { getUserEnrolledPrograms } from './enrolledPrograms';
import { rsvpApi } from './rsvp';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// Re-export for backward compatibility
export { getUserEnrolledPrograms };

/**
 * Fetch upcoming events for a list of programs
 * Returns only the earliest upcoming event per program
 */
export const getUpcomingEventsForPrograms = async (programs) => {
  const programEvents = new Map();
  const now = new Date();

  for (const program of programs) {
    try {
      const response = await fetch(`${backendUrl}/api/events/${program._id}`);
      
      if (!response.ok) {
        continue;
      }

      const events = await response.json();
      
      if (!Array.isArray(events)) {
        continue;
      }

      // Filter for upcoming events only and sort by date
      const upcomingEvents = events
        .filter(event => {
          const eventDate = new Date(event.dateTime?.start);
          return eventDate > now;
        })
        .map(event => ({
          ...event,
          programId: program._id,
          programName: program.name,
          clubId: program.clubId,
          clubName: program.clubName
        }))
        .sort((a, b) => {
          const dateA = new Date(a.dateTime?.start);
          const dateB = new Date(b.dateTime?.start);
          return dateA - dateB;
        });
      
      // Only keep the earliest upcoming event for this program
      if (upcomingEvents.length > 0) {
        programEvents.set(program._id, upcomingEvents[0]);
      }
    } catch (err) {
      // Continue on error
    }
  }

  // Convert map to array and sort by event date across all programs
  const allEarliestEvents = Array.from(programEvents.values());

  allEarliestEvents.sort((a, b) => {
    const dateA = new Date(a.dateTime?.start);
    const dateB = new Date(b.dateTime?.start);
    return dateA - dateB;
  });

  return allEarliestEvents;
};

/**
 * Get all upcoming events across all enrolled programs (grouped by Club-Program)
 * @param {boolean} includeEmpty - Whether to include programs with no upcoming events
 */
export const getUpcomingEventsByClubProgram = async (userClubs, userProfile, includeEmpty = false) => {
  try {
    const enrolledPrograms = await getUserEnrolledPrograms(userClubs, userProfile);

    if (enrolledPrograms.length === 0) {
      return [];
    }

    const now = new Date();
    const groupedEvents = new Map();

    for (const program of enrolledPrograms) {
      try {
        const response = await fetch(`${backendUrl}/api/events/${program._id}`);
        
        if (!response.ok) {
          if (includeEmpty) {
            const clubProgramKey = `${program.clubName} - ${program.name}`;
            groupedEvents.set(clubProgramKey, {
              clubName: program.clubName,
              programName: program.name,
              programId: program._id,
              clubId: program.clubId,
              events: []
            });
          }
          continue;
        }

        const events = await response.json();
        
        if (!Array.isArray(events)) {
          if (includeEmpty) {
            const clubProgramKey = `${program.clubName} - ${program.name}`;
            groupedEvents.set(clubProgramKey, {
              clubName: program.clubName,
              programName: program.name,
              programId: program._id,
              clubId: program.clubId,
              events: []
            });
          }
          continue;
        }

        // Filter for upcoming events only and sort by date
        const upcomingEvents = events
          .filter(event => {
            const eventDate = new Date(event.dateTime?.start);
            return eventDate > now;
          })
          .map(event => ({
            ...event,
            programId: program._id,
            programName: program.name,
            clubId: program.clubId,
            clubName: program.clubName
          }))
          .sort((a, b) => {
            const dateA = new Date(a.dateTime?.start);
            const dateB = new Date(b.dateTime?.start);
            return dateA - dateB;
          });
        
        // Only add programs with events (unless includeEmpty is true)
        if (upcomingEvents.length > 0 || includeEmpty) {
          const clubProgramKey = `${program.clubName} - ${program.name}`;
          groupedEvents.set(clubProgramKey, {
            clubName: program.clubName,
            programName: program.name,
            programId: program._id,
            clubId: program.clubId,
            events: upcomingEvents
          });
        }
      } catch (err) {
        if (includeEmpty) {
          const clubProgramKey = `${program.clubName} - ${program.name}`;
          groupedEvents.set(clubProgramKey, {
            clubName: program.clubName,
            programName: program.name,
            programId: program._id,
            clubId: program.clubId,
            events: []
          });
        }
      }
    }

    return Array.from(groupedEvents.values());
  } catch (err) {
    return [];
  }
};

/**
 * Get all upcoming events across all enrolled programs (legacy function - one per program)
 */
export const getUpcomingEvents = async (userClubs, userProfile, limit = 5) => {
  try {
    const enrolledPrograms = await getUserEnrolledPrograms(userClubs, userProfile);

    if (enrolledPrograms.length === 0) {
      return [];
    }

    const upcomingEvents = await getUpcomingEventsForPrograms(enrolledPrograms);

    return upcomingEvents.slice(0, limit);
  } catch (err) {
    return [];
  }
};

/**
 * Get ALL upcoming events across all enrolled programs (not limited to one per program)
 * @param {Array} userClubs - User's club memberships
 * @param {Object} userProfile - User profile data
 * @param {number} limit - Maximum number of events to return (default 50)
 * @returns {Array} All upcoming events sorted by date
 */
export const getAllUpcomingEvents = async (userClubs, userProfile, limit = 50) => {
  try {
    const enrolledPrograms = await getUserEnrolledPrograms(userClubs, userProfile);

    if (enrolledPrograms.length === 0) {
      return [];
    }

    const now = new Date();
    let allEvents = [];

    for (const program of enrolledPrograms) {
      try {
        const response = await fetch(`${backendUrl}/api/events/${program._id}`);
        
        if (!response.ok) {
          continue;
        }

        const events = await response.json();
        
        if (!Array.isArray(events)) {
          continue;
        }

        // Filter for upcoming events and add program metadata
        const upcomingEvents = events
          .filter(event => {
            const eventDate = new Date(event.dateTime?.start);
            return eventDate > now;
          })
          .map(event => ({
            ...event,
            programId: program._id,
            programName: program.name,
            clubId: program.clubId,
            clubName: program.clubName
          }));
        
        allEvents = allEvents.concat(upcomingEvents);
      } catch (err) {
        // Continue on error
      }
    }

    // Sort all events by date
    allEvents.sort((a, b) => {
      const dateA = new Date(a.dateTime?.start);
      const dateB = new Date(b.dateTime?.start);
      return dateA - dateB;
    });

    return allEvents.slice(0, limit);
  } catch (err) {
    return [];
  }
};

/**
 * Get the single next upcoming event across all enrolled programs
 * @deprecated Use getUpcomingEvents instead for multiple events
 */
export const getNextUpcomingEvent = async (userClubs, userProfile) => {
  try {
    const upcomingEvents = await getUpcomingEvents(userClubs, userProfile, 1);
    return upcomingEvents.length > 0 ? upcomingEvents[0] : null;
  } catch (err) {
    return null;
  }
};

/**
 * Get user's RSVP status for an event
 */
export const getUserRSVPStatus = async (eventId, userProfile) => {
  if (!eventId || !userProfile) {
    return null;
  }

  try {
    const rsvpData = await rsvpApi.getUserRSVP(eventId);
    
    const responseValue = rsvpData?.rsvp?.response || rsvpData?.response;
    
    if (responseValue === 'Going') {
      return 'yes';
    }
    if (responseValue === 'Not Going') {
      return 'no';
    }
    if (responseValue === 'TBD') {
      return 'maybe';
    }
    
    return responseValue || null;
  } catch (err) {
    return null;
  }
};
